<template>
  <b-container fluid>
    <div class="iq-card mt-3 px-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Event Log</h4>
        </div>

        <div class="text-right">
          <button-options :access="'Log_Event_Event'" :hideAdd="true" :hideDetail="true" :isRefreshEnabled="true"
            :isRefreshed="isRefreshed" @onRefreshed="applyFilter" class="mt-2" />
        </div>
      </div>
      <div class="iq-card-body">
        <div class="d-flex mb-1">
          <div style="margin-right: 10px; width: 300px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Select Range</span>
            <v-date-picker v-model="params.dateRange" is-range mode="datetime" :masks="{ input: 'DD-MM-YYYY HH:mm' }"
              is24hr>
              <template #default="{ inputValue, inputEvents, isDragging }">
                <div class="d-flex flex-column flex-sm-row justify-content-start align-items-center">
                  <input class="form-control bg-light border rounded date-input"
                    :class="isDragging ? 'text-muted' : 'text-dark'" :value="inputValue.start + ' - ' + inputValue.end"
                    v-on="inputEvents.start" />
                </div>
              </template>
            </v-date-picker>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Time</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntTimestamp">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Priority</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntIsPriority">
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>

          <div style="margin-right: 10px;">
            <span class="d-md-block text-muted small text-left font-weight-bold">Order Status</span>
            <b-form-select name="" class="form-control" v-model="params.order.evntStatus">
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </b-form-select>
          </div>
        </div>

        <div class="d-flex mb-2">
          <v-select v-model="params.filter.evntDeviceId" :options="devices" label="text"
            :reduce="device => device.value" placeholder="Select Device" style="width: 270px; margin-right: 10px"
            @search="onSearchDevice" />

          <v-select v-model="params.filter.evntMevtId" :options="eventTypes" label="text"
            :reduce="eventType => eventType.value" placeholder="All Event" style="width: 200px; margin-right: 10px"
            :clearable=false @search="onSearchEventType" />

          <b-form-select v-model="params.filter.evntIsPriority" style="width: 200px; margin-right: 10px">
            <template v-slot:first>
              <option value=''>All Priority</option>
            </template>
            <option value="1">High</option>
            <option value="0">Normal</option>
          </b-form-select>

          <b-form-select v-model="params.filter.evntStatus" style="width: 200px; margin-right: 10px">
            <template v-slot:first>
              <option value=''>All Status</option>
            </template>
            <option value="0">Non Active</option>
            <option value="1">Active</option>
          </b-form-select>
        </div>

        <div v-if="!params.filter.evntDeviceId" class="text-center text-warning my-3">
          <strong>Please select a device to view the event log.</strong>
        </div>

        <div v-else-if="isLoading" class="text-center my-4">
          <b-spinner variant="success" small class="align-middle"></b-spinner>
        </div>

        <div v-else-if="!canRead('log_event_event') || !data.length" class="text-center text-muted my-4">
          <strong>No data to show.</strong>
        </div>

        <div v-else>
          <b-card v-for="(group, eventType, index) in groupedData" :key="eventType" no-body class="mb-2">
            <b-card-header header-tag="header" class="p-0" role="tab">
              <b-button block v-b-toggle="`event-group-${index}`" variant="light"
                class="text-left d-flex justify-content-between align-items-center">
                <div class="d-flex flex-column" style="min-width: 0;">
                  <span class="font-weight-bold">{{ eventType }}</span>
                  <small v-if="getGroupDescription(group)" class="text-muted">
                    {{ getGroupDescription(group) }}
                  </small>
                </div>
                <b-badge variant="info" pill class="ml-2 flex-shrink-0">{{ group.length }}</b-badge>
              </b-button>
            </b-card-header>
            <b-collapse :id="`event-group-${index}`" role="tabpanel">
              <b-table striped hover fixed head-variant="dark" small responsive :fields="groupFields" :items="group"
                class="mb-0">
                <template v-slot:cell(evntIsPriority)="data">
                  <b-badge :variant="data.value == 0 ? 'success' : 'warning'">
                    {{ data.value == 0 ? 'Normal' : 'High' }}
                  </b-badge>
                </template>

                <template v-slot:cell(evntStatus)="data">
                  <b-badge :variant="data.value == 1 ? 'danger' : 'success'">
                    {{ data.value == 1 ? 'Triggered' : 'Cleared' }}
                  </b-badge>
                </template>

                <template v-slot:cell(reading)="data">
                  <div v-if="extractReadings(data.item).length" class="reading-list">
                    <div v-for="r in extractReadings(data.item)" :key="r.param" class="reading-item">
                      <span class="reading-param">{{ r.param }}:</span>
                      <span class="reading-value" :class="{ 'reading-breached': r.breached }">
                        {{ formatNum(r.actual) }}
                      </span>
                      <span class="reading-rule text-muted">({{ r.operator }} {{ r.threshold }})</span>
                    </div>
                  </div>
                  <span v-else class="text-muted small">—</span>
                </template>

                <template v-slot:cell(action)="data">
                  <div class="text-center">
                    <button class="btn btn-sm btn-outline-primary mx-1" @click="openThresholdDetail(data.item)">
                      Detail
                    </button>
                  </div>
                </template>
              </b-table>
            </b-collapse>
          </b-card>
        </div>
      </div>
    </div>

    <b-modal size="md" hide-footer no-close-on-esc no-close-on-backdrop v-model="isOpenThresholdDetail">
      <template #modal-header>
        <h5 class="modal-title">Thresholds Data</h5>
        <b-button variant="outline-danger" size="sm" @click="isOpenThresholdDetail = false">
          <i class="ri-close-fill"></i>
        </b-button>
      </template>
      <b-container class="my-4">
        <b-row>
          <b-col md="12" v-for="(item, name) in thresholdDetail" :key="name" class="mb-3">
            <b-card>
              <template #header>
                <div class="d-flex justify-content-between align-items-center">
                  <strong>{{ name }}</strong>
                  <b-badge v-if="item.conditions_operator" variant="info">
                    {{ item.conditions_operator }}
                  </b-badge>
                </div>
              </template>

              <b-table small :items="formatTableData(item.data)" :fields="[
                { key: 'parameter', label: 'Parameter' },
                { key: 'operator', label: 'Operator' },
                { key: 'value', label: 'Value' }
              ]" responsive="sm" bordered head-variant="light" class="mb-0">
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
  </b-container>
</template>

<script>
import ButtonOptions from '@/components/directives/button-options';
import EventLogSvc from '@/services/EventLogSvc';
import EventSvc from '@/services/EventSvc';
import DeviceSvc from '@/services/DeviceSvc';
import requestBuilder from '@/util/request.builder';
import { debounce } from 'lodash';
import moment from 'moment';

export default {
  name: 'EventLog',
  components: {
    ButtonOptions,
  },
  data() {
    return {
      data: [],
      devices: [],
      eventTypes: [],
      isLoading: false,
      isRefreshed: false,
      isReady: false,
      isOpenThresholdDetail: false,
      thresholdDetail: {},
      params: {
        filter: {
          evntDeviceId: '',
          evntIsPriority: '',
          evntStatus: '',
          evntMevtId: '',
        },
        order: {
          evntIsPriority: '',
          evntStatus: '',
          evntTimestamp: 'desc',
        },
        dateRange: {
          start: moment().subtract(24, 'hours').startOf('hour').toISOString(),
          end: moment().endOf('hour').toISOString(),
        },
      },
      pagination: {
        current: 1,
        pageSize: 9999,
        totalSize: 0,
      },
      groupFields: [
        {
          key: 'evntTimestamp',
          label: 'Date Time',
          class: 'text-left',
          thStyle: { minWidth: '160px' },
          formatter: (value) => {
            const date = moment.utc(value).local();
            return `${date.format('YYYY-MM-DD, HH:mm:ss')}`;
          },
        },
        {
          key: 'evntCreatedTime',
          label: 'Received Time',
          class: 'text-left',
          thStyle: { minWidth: '160px' },
          formatter: (value) => {
            const date = moment.utc(value).local();
            return `${date.format('YYYY-MM-DD, HH:mm:ss')}`;
          },
        },
        {
          key: 'evntStatus',
          label: 'State',
          class: 'text-left',
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'evntIsPriority',
          label: 'Priority',
          class: 'text-left',
          thStyle: { minWidth: '100px' },
        },
        {
          key: 'evntDuration',
          label: 'Duration',
          class: 'text-left',
          thStyle: { minWidth: '90px' },
          formatter: (value) => {
            const duration = moment.duration(value, 'seconds');
            return `${duration.hours().toString().padStart(2, '0')}:${duration.minutes().toString().padStart(2, '0')}:${duration.seconds().toString().padStart(2, '0')}`;
          },
        },
        {
          key: 'reading',
          label: 'Reading',
          class: 'text-left',
          thStyle: { minWidth: '240px' },
        },
        {
          key: 'Action',
          label: 'Detail',
          class: 'text-center',
          thStyle: { minWidth: '90px' },
        },
      ],
    };
  },
  computed: {
    groupedData() {
      if (!this.data || !this.data.length) return {};
      return this.data.reduce((acc, item) => {
        const eventType = item.evntMevt?.mevtType || 'Unknown';
        if (!acc[eventType]) acc[eventType] = [];
        acc[eventType].push(item);
        return acc;
      }, {});
    },
  },
  watch: {
    params: {
      deep: true,
      handler() {
        if (!this.isReady) return;
        this.pagination.current = 1;
        this.debouncedApplyFilter();
      },
    },
  },
  methods: {
    async getData({
      start, end, finds, orders,
    }) {
      try {
        this.isLoading = true;
        this.isRefreshed = true;

        const Response = await EventLogSvc.getEventLogs({
          start,
          end,
          finds,
          orders,
          params: { ...requestBuilder.buildFilters(this.pagination, {}) },
        });

        this.data = Response.data;
        this.pagination.totalSize = Response.pagination.totalData;
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    async getDevices(deviceId) {
      const queryParts = ['finds[devcType][]=gateway'];
      if (deviceId) queryParts.push(`finds[devcUniqueId][]=${deviceId}`);
      const Response = await DeviceSvc.getDevices({}, queryParts.join('&'));

      this.devices = Response.data.data.map((item) => ({
        value: item.devcUniqueId,
        text: item.devcMass?.massName,
      }));
    },
    async getEventTypes(mevtType) {
      const params = {};
      if (mevtType) params['finds[mevtType]'] = mevtType;
      const Response = await EventSvc.get(params);
      this.eventTypes = Response.data.map((item) => ({
        value: item.mevtId,
        text: item.mevtType,
      }));
    },
    onSearchDevice(searchText) {
      if (searchText) {
        this.getDevices(searchText);
      }
    },
    onSearchEventType(searchText) {
      if (searchText) {
        this.getEventTypes(searchText);
      }
    },
    applyFilter() {
      if (!this.params.filter.evntDeviceId) {
        this.data = [];
        return;
      }

      this.getData({
        start: moment(this.params.dateRange.start).unix(),
        end: moment(this.params.dateRange.end).unix(),
        finds: this.params.filter,
        orders: this.params.order,
      });
    },
    openThresholdDetail(data) {
      try {
        this.thresholdDetail = JSON.parse(data.evntThreshold);
        this.isOpenThresholdDetail = true;
      } catch (e) {
        this.$toast.error('Failed to parse threshold data');
      }
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData({
        start: moment(this.params.dateRange.start).unix(),
        end: moment(this.params.dateRange.end).unix(),
        finds: this.params.filter,
        orders: this.params.order,
      });
    },
    formatTableData(data) {
      return Object.keys(data).map((key) => ({
        parameter: key,
        operator: data[key].operator,
        value: data[key].value,
      }));
    },
    getGroupDescription(group) {
      return group?.[0]?.evntMevt?.mevtDescription || '';
    },
    flattenLeaves(obj, out = {}) {
      if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([k, v]) => {
          if (v && typeof v === 'object') this.flattenLeaves(v, out);
          else out[k] = v;
        });
      }
      return out;
    },
    checkBreach(actual, operator, threshold) {
      if (actual === null || actual === undefined) return false;
      const a = Number(actual);
      const t = Number(threshold);
      if (Number.isNaN(a) || Number.isNaN(t)) return false;
      switch (operator) {
        case '>': return a > t;
        case '>=': return a >= t;
        case '<': return a < t;
        case '<=': return a <= t;
        case '==': return a === t;
        case '!=': return a !== t;
        default: return false;
      }
    },
    extractReadings(log) {
      let threshold;
      let newData;
      try {
        threshold = JSON.parse(log.evntThreshold || '{}');
        newData = JSON.parse(log.evntNewData || '{}');
      } catch (e) {
        return [];
      }
      const flat = this.flattenLeaves(newData);
      const readings = [];
      Object.values(threshold).forEach((cond) => {
        if (!cond || typeof cond.data !== 'object') return;
        Object.entries(cond.data).forEach(([param, rule]) => {
          if (!rule || rule.operator === undefined || rule.value === undefined) return;
          const actual = flat[param];
          readings.push({
            param,
            actual,
            operator: rule.operator,
            threshold: rule.value,
            breached: this.checkBreach(actual, rule.operator, rule.value),
          });
        });
      });
      return readings;
    },
    formatNum(v) {
      if (v === null || v === undefined) return '—';
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      if (Number.isInteger(n)) return String(n);
      return n.toFixed(2);
    },
  },
  async mounted() {
    await this.getDevices();
    this.getEventTypes();
    this.applyFilter();
    this.$nextTick(() => {
      this.isReady = true;
    });
  },
  created() {
    this.onSearchDevice = debounce(this.onSearchDevice, 300);
    this.debouncedApplyFilter = debounce(this.applyFilter, 400);
  },
};
</script>

<style scoped>
.reading-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
}

.reading-item {
  white-space: nowrap;
}

.reading-param {
  color: #495057;
  font-weight: 600;
  margin-right: 4px;
}

.reading-value {
  font-weight: 600;
  color: #28a745;
  margin-right: 4px;
}

.reading-value.reading-breached {
  color: #dc3545;
}

.reading-rule {
  font-size: 0.75rem;
}
</style>
